import { factory } from './base';
import { snakify } from '../helpers/common';

export interface LoginForm {
  email: string;
  password: string;
}

export interface ConfirmLogin {
  otp_attempt: string;
}

export type UserStatus = {
  name: string;
  id: string;
};

export type User = {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  mobilePhone?: string | null;
  profession: null;
  language: string;
  statuses: UserStatus[];

  address?: string | null;
  country: string | null;
  state?: string | null;
  city?: string | null;
  currency: string;
  postalCode: string | null;

  canIssueBonuses: boolean;
  depositBonusCode: string | null;

  receivePromos?: boolean;
  receiveSmsPromos?: boolean;

  createdAt: string;
  confirmedAt: string;
  currentSignInAt: string;

  authFieldsMissed: any[];
  autologinLink: null;
  autoregistered: boolean;
  twoFactorEnabled: boolean;
  verified: boolean;

  mobilePhoneWithoutDialCode?: string | null;
};

export interface Wallet {
  amountCents: number;
  availableToCashoutCents: number;
  availableToBetWithLockedCents: number;
  currency: string;
  active: boolean;
}

export const register = factory((r) => (user: object) =>
  r<User>('post', 'users', snakify({ user }))
);

export const login = factory((r) => (user: LoginForm | ConfirmLogin) =>
  r<User>('post', 'users/sign_in', snakify({ user }))
);

export const logout = factory((r) => () => r<null>('delete', 'users/sign_out'));

export const player = factory((r) => () => r<User>('get', 'player'));

export enum UpdatePlayerContext {
  Edition = 'edition',
}

export interface UpdatePlayer {
  context: UpdatePlayerContext;
  player: Partial<User>;
}

export const updatePlayer = factory((r) => (data: UpdatePlayer) =>
  r<null>('patch', 'player', snakify(data))
);

export const playerAccounts = factory((r) => (compatibility: boolean = true) =>
  r<Wallet[]>(
    'get',
    'player/accounts',
    !compatibility ? { compatibility } : undefined
  )
);

export const setPlayerAccounts = factory(
  (r) => (currency: Wallet['currency']) =>
    r<Wallet[]>('post', 'player/accounts', snakify({ currency }))
);

export const deletePlayerAccount = factory(
  (r) => (currency: Wallet['currency']) =>
    r<Wallet[]>('delete', `player/accounts/${currency}`)
);

export interface RecoveryForm {
  email: string;
}

export interface NewPassword {
  password: string;
  passwordConfirmation: string;
}

export const initRecoveryPassword = factory((r) => (user: RecoveryForm) =>
  r<null>('post', 'users/password', snakify({ errors: {}, user }))
);

export const recoveryPassword = factory(
  (r) => (user: NewPassword & { resetPasswordToken: string }) =>
    r<null>('put', 'users/password', snakify({ errors: {}, user }))
);

export const updateBonusSettings = factory((r) => (canIssue: boolean) =>
  r<null>('patch', 'player/update_bonus_settings', snakify({ canIssue }))
);

export interface DepositBonusCode {
  depositBonusCode: string;
}

export const setBonusCode = factory((r) => (values: DepositBonusCode) =>
  r<null>('patch', 'player/set_bonus_code', snakify(values))
);

export const clearBonusCode = factory((r) => () =>
  r<null>('delete', 'player/clear_bonus_code')
);

export const confirmationEmail = factory((r) => (token: string) =>
  r<null>('get', `users/confirmation?confirmation_token=${token}`)
);

export const confirmationCashoutAddress = factory(
  (r) => (params: { paymentId: string; token: string }) =>
    r<{ errors?: any }>(
      'post',
      'payment_processing/confirm_cashout_address',
      snakify(params)
    )
);

export interface CompPoints {
  chargeable?: {
    points: number;
  };
  persistent?: {
    points: number;
  };
}

export const compPoints = factory((r) => () =>
  r<CompPoints>('get', 'player/comp_points')
);

export const unlockAccount = factory((r) => (token: string) =>
  r<null>('get', `users/unlock?unlock_token=${token}`)
);
